import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { ChevronDown, MapPin, X } from "lucide-react";

/**
 * CityAutocomplete: limited to cities InterQuest actually serves (with
 * accommodations). Uses the catalog's station_id for the plan endpoint.
 */
export default function CityAutocomplete({ value, onChange, placeholder = "Pick a city", testId }) {
  const [cities, setCities] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data));
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = query.trim()
    ? cities.filter((c) => (c.name + " " + c.country).toLowerCase().includes(query.toLowerCase()))
    : cities;

  const pick = (c) => {
    onChange({ id: c.station_id, name: c.station_name, city: c });
    setQuery("");
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange(null);
    setQuery("");
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        className="iq-input text-left flex items-center justify-between gap-2"
      >
        <span className={`truncate ${value ? "text-cream" : "text-cream-dim/40"}`}>
          {value ? `${value.city?.name || value.name}` : placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {value && (
            <X onClick={clear} className="h-4 w-4 text-cream-dim/70 hover:text-cream" />
          )}
          <ChevronDown className={`h-4 w-4 text-gold transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-panel border border-panel-border rounded-sm shadow-xl shadow-black/40 max-h-96 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-panel-border">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter cities…"
              className="iq-input text-sm py-2"
              data-testid={`${testId}-filter`}
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-cream-dim text-sm font-serif italic">
                No cities match "{query}".
              </div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => pick(c)}
                  className="w-full text-left px-4 py-2.5 hover:bg-panel-border flex items-center justify-between gap-3 border-b border-panel-border/40 last:border-0"
                  data-testid={`${testId}-option-${c.slug}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <MapPin className="h-4 w-4 text-gold shrink-0" />
                    <div className="min-w-0">
                      <div className="text-cream text-sm truncate">{c.name}</div>
                      <div className="text-cream-dim/70 text-[11px]">{c.country} · {c.station_name}</div>
                    </div>
                  </div>
                  <span className="text-[10px] text-gold/80 font-display tracking-widest">+{c.xp} XP</span>
                </button>
              ))
            )}
          </div>
          <div className="px-3 py-2 border-t border-panel-border bg-ink/40 text-[10px] text-cream-dim/70 font-serif italic">
            Showing {filtered.length} of {cities.length} curated InterQuest cities.
          </div>
        </div>
      )}
    </div>
  );
}
